import { FC } from "react";
import { Link } from "react-router-dom";
interface PaginationConfig {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  pageSizeOptions: number[];
}

interface IonizableLipidResult {
  index: number;
  experimental_id: string;
  sum: number;
  il_name: string;
  il_smiles: string;
  il_head_name: string;
  il_head_smiles: number | null;
  il_linker_name: string | null;
  il_linker_smiles: number | null;
  il_tail1_name: string;
  il_tail1_smiles: number | null;
  il_tail2_name: string | null;
  il_tail2_smiles: number | null;
}

interface TableData {
  results: IonizableLipidResult[];
  count: number;
}

interface TableHeaderProps {}

interface TableRowProps {
  result: IonizableLipidResult;
  index: number;
}

interface TablePaginationProps {
  paginationConfig: PaginationConfig;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

interface IonizableLipidTableProps {
  data: TableData | null;
}

const TableHeader: FC<TableHeaderProps> = () => {
  return (
    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
      <tr>
        <th
          rowSpan={2}
          scope="col"
          className="px-4 py-3 font-semibold whitespace-nowrap"
        >
          Library Code
        </th>
        <th
          rowSpan={2}
          scope="col"
          className="px-4 py-3 font-semibold whitespace-nowrap"
        >
          IL Name
        </th>
        <th
          rowSpan={2}
          scope="col"
          className="px-4 py-3 font-semibold whitespace-nowrap"
        >
          # Formulation
        </th>
        <th
          colSpan={5}
          scope="col"
          className="px-4 py-3 font-semibold text-center"
        >
          <div className="whitespace-nowrap">Structure</div>
        </th>
      </tr>
      <tr className="text-xs text-gray-700 uppercase bg-gray-50">
        <th scope="col" className="px-4 py-3 font-semibold">
          Whole
        </th>
        <th scope="col" className="px-4 py-3 font-semibold">
          Head
        </th>
        <th scope="col" className="px-4 py-3 font-semibold">
          Linker
        </th>
        <th scope="col" className="px-4 py-3 font-semibold">
          Tail1
        </th>
        <th scope="col" className="px-4 py-3 font-semibold">
          Tail2
        </th>
      </tr>
    </thead>
  );
};

const TableRow: FC<TableRowProps> = ({ result, index }) => {
  const getSubunitImagePath = (
    experimentalId: string,
    subunitType: string,
    subunitName: string | null
  ) => {
    if (!subunitName) return "";

    // const numericPart = extractNumbers(subunitName);
    return `/subunit/${experimentalId}/${subunitType}/${subunitName}.svg`;
  };
  return (
    <tr className="items-center justify-center bg-white border-b hover:bg-gray-50 dark:hover:bg-gray-50">
      <td className="px-4 py-3 text-sm font-medium text-center text-blue-600 whitespace-nowrap md:text-base">
        <Link
          to={`/search/text?filter=${encodeURIComponent(
            JSON.stringify({ experiment_id: result.experimental_id })
          )}`}
          target="_blank"
          className="block p-2 font-medium text-blue-600 hover:underline"
        >
          {result.experimental_id}
        </Link>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-center whitespace-nowrap md:text-base">
        {result.il_name}
      </td>
      <td className="px-4 py-3 text-sm text-center md:text-base">
        {result.sum}
      </td>
      <td className="px-4 py-3 text-sm text-center align-middle transition-colors hover:bg-gray-100 md:text-base">
        {result.index ? (
          <img
            src={`/ionizableLipid/LNP_${String(result.index).padStart(
              7,
              "0"
            )}.svg`}
            alt={`Complete structure of ${result.il_name}`}
            className="w-40 mx-auto"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = "none";
              if (img.parentElement) {
                img.parentElement.textContent = "None";
              }
            }}
          />
        ) : (
          "None"
        )}
      </td>
      <td className="px-4 py-3 text-sm text-center align-middle transition-colors hover:bg-gray-100 md:text-base">
        {result.il_head_name ? (
          <img
            src={`/subunit/${result.experimental_id}/head/${result.il_head_name}.svg`}
            alt={`Head ${result.il_head_name}`}
            className="w-40 mx-auto"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = "none";
              if (img.parentElement) {
                img.parentElement.textContent = "None";
              }
            }}
          />
        ) : (
          "None"
        )}
      </td>
      <td className="px-4 py-3 text-sm text-center align-middle transition-colors hover:bg-gray-100 md:text-base">
        {result.il_linker_name ? (
          <img
            src={getSubunitImagePath(
              result.experimental_id,
              "linker",
              result.il_linker_name
            )}
            alt={`${result.il_linker_name}`}
            className="w-40 mx-auto"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = "none";
              if (img.parentElement) {
                img.parentElement.textContent = "None";
              }
            }}
          />
        ) : (
          "None"
        )}
      </td>
      <td className="px-4 py-3 text-sm text-center align-middle transition-colors hover:bg-gray-100 md:text-base">
        {result.il_tail1_name ? (
          <img
            src={getSubunitImagePath(
              result.experimental_id,
              "tail1",
              result.il_tail1_name
            )}
            alt={`Tail1 ${result.il_tail1_name}`}
            className="w-40 mx-auto"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = "none";
              if (img.parentElement) {
                img.parentElement.textContent = "None";
              }
            }}
          />
        ) : (
          "None"
        )}
      </td>
      <td className="px-4 py-3 text-sm text-center align-middle transition-colors hover:bg-gray-100 md:text-base">
        {result.il_tail2_name ? (
          <img
            src={`/subunit/${result.experimental_id}/tail2/${result.il_tail2_name}.svg`}
            alt={`${result.il_tail2_name}`}
            className="w-40 mx-auto"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = "none";
              if (img.parentElement) {
                img.parentElement.textContent = "None";
              }
            }}
          />
        ) : (
          "None"
        )}
      </td>
    </tr>
  );
};

const IonizableLipidTable: FC<IonizableLipidTableProps> = ({ data }) => {
  return (
    <div className="relative">
      {data?.results && data.results.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-center text-gray-500">
              <TableHeader />
              <tbody className="divide-y divide-gray-200">
                {data.results.map((result, index) => (
                  <TableRow
                    key={`${result.experimental_id}-${result.il_name}-${index}`}
                    result={result}
                    index={index}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export { IonizableLipidTable };
export type {
  IonizableLipidResult,
  IonizableLipidTableProps,
  PaginationConfig,
  TableData,
  TableHeaderProps,
  TablePaginationProps,
  TableRowProps,
};
